import { createAsyncThunk } from '@reduxjs/toolkit';
import api from 'components/api';
import { CustomError } from 'views/helpers';
import { loadItemsFulfilled } from '../helpers';

export const createRefreshOfAccountingEntity = entityName =>
  createAsyncThunk(`${entityName.toLowerCase()}s/refresh`, async (orgId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/org/${orgId}/${entityName.toLowerCase()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(new CustomError(error));
    }
  });
export const generateCodeForRefreshOfAccountingEntity = entityName => {
  const refresh = createRefreshOfAccountingEntity(entityName);
  return {
    [`refresh${entityName}s`]: refresh,
    generatedExtraReducers: {
      [refresh.fulfilled]: loadItemsFulfilled
    }
  };
};

export const createSyncWithXeroOfAccountingEntity = entityName =>
  createAsyncThunk(
    `${entityName.toLowerCase()}s/syncWithXero`,
    async ({ orgId, xeroCompanyId } = {}, { rejectWithValue }) => {
      try {
        const params = xeroCompanyId ? `?xeroCompanyId=${xeroCompanyId}` : '';
        await api.get(`/org/${orgId}/${entityName.toLowerCase()}/xero_sync${params}`);
        return { success: true, orgId };
      } catch (error) {
        return rejectWithValue(new CustomError(error));
      }
    }
  );

export const generateCodeForSyncWithXeroOfAccountingEntity = entityName => {
  const thunk = createSyncWithXeroOfAccountingEntity(entityName);

  return {
    [`sync${entityName}sWithXero`]: thunk,
    generatedExtraReducers: builder => {
      builder
        .addCase(thunk.pending, state => {
          state.isXeroSyncing = true;
          state.xeroSyncError = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.isXeroSyncing = false;
          state.xeroSyncError = null;
          state.lastXeroSyncOrgId = action.payload.orgId;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.isXeroSyncing = false;
          state.xeroSyncError = action.payload || 'Xero sync failed';
        });
    }
  };
};

export const createSyncWithQuickbooksOfAccountingEntity = entityName =>
  createAsyncThunk(
    `${entityName.toLowerCase()}s/syncWithQuickbooks`,
    async (orgId, { rejectWithValue }) => {
      try {
        await api.get(`/org/${orgId}/${entityName.toLowerCase()}/quickbooks_sync`);
        return { success: true };
      } catch (error) {
        return rejectWithValue(new CustomError(error));
      }
    }
  );
export const generateCodeForSyncWithQuickbooksOfAccountingEntity = entityName => {
  const thunk = createSyncWithQuickbooksOfAccountingEntity(entityName);

  return {
    [`sync${entityName}sWithQuickbooks`]: thunk,
    generatedExtraReducers: builder => {
      builder
        .addCase(thunk.pending, state => {
          state.isQuickbooksSyncing = true;
          state.quickbooksSyncError = null;
        })
        .addCase(thunk.fulfilled, state => {
          state.isQuickbooksSyncing = false;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.isQuickbooksSyncing = false;
          state.quickbooksSyncError = action.payload || 'QuickBooks sync failed';
        });
    }
  };
};

export const createExportOfAccountingEntity = entityName => async orgId => {
  const response = await api.post(`/org/${orgId}/export${entityName}s`, {});
  return response.data;
};

export const createImportOfAccountingEntity = entityName => async (orgId, fileId) => {
  const response = await api.post(`/org/${orgId}/import${entityName}s`, {
    fileId
  });
  return response.data;
};

export const generateCommonCodeForAccountingEntity = entityName => {
  const {
    generatedExtraReducers: refreshGeneratedExtraReducers,
    ...refresh
  } = generateCodeForRefreshOfAccountingEntity(entityName);

  const {
    generatedExtraReducers: syncWithXeroGeneratedExtraReducers,
    ...syncWithXero
  } = generateCodeForSyncWithXeroOfAccountingEntity(entityName);

  const {
    generatedExtraReducers: syncWithQuickbooksGeneratedExtraReducers,
    ...syncWithQuickbooks
  } = generateCodeForSyncWithQuickbooksOfAccountingEntity(entityName);

  return {
    ...refresh, // [`refresh${entityName}s`]
    ...syncWithXero, // [`sync${entityName}sWithXero`]
    ...syncWithQuickbooks, // [`sync${entityName}sWithQuickbooks`]
    [`export${entityName}s`]: createExportOfAccountingEntity(entityName),
    [`import${entityName}s`]: createImportOfAccountingEntity(entityName),
    generatedExtraReducers: {
      ...refreshGeneratedExtraReducers,
      ...syncWithXeroGeneratedExtraReducers,
      ...syncWithQuickbooksGeneratedExtraReducers
    }
  };
};
